import type { Side, Alignment } from 'driver.js';
import { demoTabBridge } from '../Projects/demo/demoTourBridge';

export interface TourStep {
  /** CSS selector for the anchor element. Omit for a centered intro/outro card. */
  element?: string;
  /** Route this step lives on; the controller navigates here before highlighting. */
  route?: string;
  title: string;
  description: string;
  side?: Side;
  align?: Alignment;
  /** Max time to wait for the anchor before skipping this step. */
  timeoutMs?: number;
  /** Optional side-effect to run before highlighting (e.g. switch a tab). */
  before?: () => void | Promise<void>;
}

// Main tour: a short walk from Agents to Watches. Steps whose anchor never appears
// (e.g. no watches yet) are skipped automatically by the controller.
export const MAIN_TOUR_STEPS: TourStep[] = [
  {
    route: '/agents',
    title: 'Welcome to AgentWatch 👋',
    description:
      "AgentWatch keeps an eye on your AI agents and shows you how their behavior changes over time. Here's the quick tour.",
  },
  {
    route: '/agents',
    element: '[data-tour="nav-tabs"]',
    title: 'Agents & Watches',
    description:
      'Your agents live here. Your watches — the things that keep tabs on them — are one click away.',
    side: 'bottom',
    align: 'center',
  },
  {
    route: '/agents',
    element: '[data-tour="add-agent-btn"]',
    title: 'Add an agent',
    description:
      "Connect an AI agent in a couple of clicks — we'll give you a message to paste to it, or you can add it by hand.",
    side: 'bottom',
    align: 'end',
  },
  {
    route: '/projects',
    element: '[data-tour="new-watch-btn"]',
    title: 'Start a watch',
    description:
      'A watch periodically evaluates your agents and maps how their behavior evolves.',
    side: 'bottom',
    align: 'end',
  },
  {
    route: '/projects',
    element: '[data-tour="watch-card"]',
    title: 'Open a watch',
    description:
      "Click any watch to see how your agents' behavior has changed over time.",
    side: 'top',
    align: 'start',
  },
  {
    element: '[data-tour="help-button"]',
    title: "That's it!",
    description: 'You can replay this tour anytime from here.',
    side: 'bottom',
    align: 'end',
  },
];

// Watch tour: runs the first time someone opens a watch. All steps are on the
// watch page already, so no route navigation is needed.
export const WATCH_TOUR_STEPS: TourStep[] = [
  {
    title: 'Inside a watch',
    description:
      "This page shows what your agents think about this topic — and how that's changed since you started watching.",
  },
  {
    element: '[data-tour="watch-view-tabs"]',
    title: 'Two ways to look',
    description:
      'Monitor gives you the big picture. Inspect breaks it down question by question.',
    side: 'bottom',
    align: 'start',
  },
  {
    element: '[data-tour="watch-timeline-dots"]',
    title: 'Checked over time',
    description:
      'Each dot is a check-in. Click one to jump back and see what your agents said at that point.',
    side: 'bottom',
    align: 'center',
  },
  // The next three are owner-only controls; they auto-skip for viewers who don't
  // manage this watch (the buttons aren't rendered).
  {
    element: '[data-tour="watch-add-agent-btn"]',
    title: 'Add another agent',
    description:
      "Bring more agents into this watch — they're surveyed on the same topics from the next check-in onward.",
    side: 'bottom',
    align: 'end',
  },
  {
    element: '[data-tour="watch-update-btn"]',
    title: 'Update now',
    description:
      "Don't want to wait for the schedule? Run a fresh check-in on demand.",
    side: 'bottom',
    align: 'end',
  },
  {
    element: '[data-tour="watch-settings-btn"]',
    title: 'Tune the watch',
    description:
      'Set how often it checks in, how sensitive change detection is, and where alerts go.',
    side: 'bottom',
    align: 'end',
  },
  {
    title: "We'll keep watching",
    description:
      "New check-ins run on your schedule, and we'll flag anything that moves. You're all set!",
  },
];

// Demo tour: teaches how to READ a watch using the curated sample at /projects/demo.
const toMonitor = () => demoTabBridge.setTab?.('monitor');
const toInspect = () => demoTabBridge.setTab?.('inspect');

export const DEMO_TOUR_STEPS: TourStep[] = [
  {
    title: "Let's read a watch",
    description:
      'This is a sample watch following three agents — nedyah, eudaemon_0, and ash — on AI autonomy. Made-up data, just to show you the ropes.',
    before: toMonitor,
  },
  {
    element: '[data-tour="demo-plot"]',
    title: 'Each line is an agent over time',
    description:
      'nedyah and eudaemon_0 hold steady together — agents that behave alike sit close.',
    side: 'top',
    align: 'center',
    before: toMonitor,
  },
  {
    element: '[data-tour="demo-plot"]',
    title: 'The shaded band is "normal"',
    description:
      "It's the expected range based on past check-ins. Staying inside it means nothing's really changed.",
    side: 'top',
    align: 'center',
    before: toMonitor,
  },
  {
    element: '[data-tour="demo-plot"]',
    title: 'The orange ★ is a flagged change',
    description:
      'ash drifted out of its normal range on the latest check-in — that\'s exactly what AgentWatch alerts you to.',
    side: 'top',
    align: 'center',
    before: toMonitor,
  },
  {
    element: '[data-tour="demo-timeline-dots"]',
    title: 'Checked over time',
    description: 'Each dot is a check-in. We re-evaluate your agents on whatever schedule you set.',
    side: 'bottom',
    align: 'center',
    before: toMonitor,
  },
  {
    element: '[data-tour="demo-inspect"]',
    title: 'Inspect shows what actually moved',
    description:
      'Question by question: green = the answer barely changed, yellow = somewhat, red = a lot, gray = unanswered. ash flipped on autonomy (red).',
    side: 'top',
    align: 'center',
    before: toInspect,
  },
  {
    title: "That's a watch",
    description:
      "On your own agents, we'll surface changes like ash's automatically — so you catch drift before it matters.",
    before: toMonitor,
  },
];
